import { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import TallyExportPreviewDialog from "@/components/TallyExportPreviewDialog";
import { formatInr } from "@/utils/format-inr";
import type { TallyExportPreview } from "@/utils/tally-export-preview";

import {
  bankSkippedToTallySkipped,
  buildBankTallyPreviewSummary,
  tryBuildBankTallyExportPreview,
} from "./bank-tally-export-preview";
import type { BankTallyMapping } from "./build-bank-tally-vouchers";
import type { BankCreditTxn, BankSkippedRow } from "./bank-statement-types";

const DEFAULT_PREFIX = "";
const DEFAULT_SEED = "0001";

export type BankTallyExportPreviewDialogProps = {
  open: boolean;
  onClose: () => void;
  credits: BankCreditTxn[];
  skipped: BankSkippedRow[];
  mapping: BankTallyMapping;
  filenamePrefix?: string;
};

export default function BankTallyExportPreviewDialog({
  open,
  onClose,
  credits,
  skipped,
  mapping,
  filenamePrefix = "bank-tally-export",
}: BankTallyExportPreviewDialogProps) {
  const [prefix, setPrefix] = useState(DEFAULT_PREFIX);
  const [seed, setSeed] = useState(DEFAULT_SEED);

  useEffect(() => {
    if (open) {
      setPrefix(DEFAULT_PREFIX);
      setSeed(DEFAULT_SEED);
    }
  }, [open]);

  const summary = useMemo(() => buildBankTallyPreviewSummary(credits, skipped), [credits, skipped]);

  const tallySkipped = useMemo(() => bankSkippedToTallySkipped(skipped), [skipped]);

  const built = useMemo(
    () =>
      tryBuildBankTallyExportPreview({
        credits,
        skipped,
        mapping,
        prefix,
        seed,
      }),
    [credits, skipped, mapping, prefix, seed]
  );

  const initialPreview: TallyExportPreview = useMemo(() => {
    if (built.ok) return built.preview;
    return { rows: [], skipped: tallySkipped };
  }, [built, tallySkipped]);

  const totalNote = useMemo(() => {
    const parts = [
      `${summary.creditCount} credit${summary.creditCount === 1 ? "" : "s"}`,
      `${formatInr(summary.creditTotal)} total`,
    ];
    if (summary.skippedCount > 0) {
      parts.push(
        `${summary.skippedCount} row${summary.skippedCount === 1 ? "" : "s"} skipped${
          summary.debitSkippedCount > 0 ? ` (${summary.debitSkippedCount} debit)` : ""
        }`
      );
    }
    return parts.join(" · ");
  }, [summary]);

  if (!open) return null;

  return (
    <TallyExportPreviewDialog
      open={open}
      onClose={onClose}
      source="prebuilt"
      filenamePrefix={filenamePrefix}
      totalNote={totalNote}
      initialPreview={initialPreview}
      includedCount={summary.creditCount}
      includedNoun="credit(s)"
      manualFormDefaults={{
        incomeLedger: mapping.crLedger,
        drLedger: mapping.drLedger,
        costCategory: mapping.costCategory,
        costCentre: mapping.costCentre,
        paymentMode: "ONLINE",
      }}
      headerExtra={
        <Box>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Receipt prefix"
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
                fullWidth
                size="small"
                helperText="Optional text before the sequential number"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Seed"
                value={seed}
                onChange={(e) => setSeed(e.target.value)}
                fullWidth
                size="small"
                required
                error={!built.ok}
                helperText={
                  built.ok ? "Leading zeros set pad width (e.g. 0007 → 0007, 0008…)" : built.message
                }
              />
            </Grid>
          </Grid>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: summary.skippedByReason.length ? 1 : 0 }}
          >
            Mapping: Dr {mapping.drLedger.trim()} → Cr {mapping.crLedger.trim()} ·{" "}
            {mapping.costCategory.trim()} / {mapping.costCentre.trim()}
          </Typography>
          {summary.skippedByReason.length > 0 ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              <Typography variant="body2" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
                Skipped rows
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {summary.skippedByReason.map((item) => (
                  <li key={item.reason}>
                    {item.reason}: {item.count}
                  </li>
                ))}
              </Box>
            </Alert>
          ) : null}
        </Box>
      }
    />
  );
}
